import { Alert, List, ListItem, ListItemText, Box, Avatar, Typography, Divider, Paper, Container, Button, Stack, TextField, IconButton, Snackbar, FormControl, InputLabel, Select, MenuItem, Checkbox, FormControlLabel, Tooltip, Chip, ListItemAvatar } from "@mui/material";
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Grid from '@mui/material/Grid2';
import LoadingButton from '@mui/lab/LoadingButton';
import React, { useContext, useEffect, useState } from "react";
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import { AuthContext, SetPageTitle } from "../../lib/core";
import { getAuth } from "firebase/auth";
import { collection, query, getDocs, doc, setDoc, addDoc, orderBy, serverTimestamp, getDoc } from 'firebase/firestore';
import CircularProgress from '@mui/material/CircularProgress';
import { httpsCallable } from "firebase/functions";
import {AdminPageContext} from "./AdminPageContext";
import {PageSection} from "../../template/TemplateElements";
import PeopleIcon from '@mui/icons-material/People';
import CheckIcon from '@mui/icons-material/Check';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import CancelIcon from '@mui/icons-material/Cancel';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import RefreshIcon from '@mui/icons-material/Refresh';
import SearchIcon from '@mui/icons-material/Search';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import PublicIcon from '@mui/icons-material/Public';
import FactCheckIcon from '@mui/icons-material/FactCheck';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import Menu from '@mui/material/Menu';

export const AdminPanel = () => {
    const auth = getAuth();
    const { firestoreInstance, functionsInstance, authUser } = useContext(AuthContext);
    const [loaded, setLoaded] = useState(false);
    const [error, setError] = useState(null);
    const [nurses, setNurses] = useState([]);
    const [selectedNurse, setSelectedNurse] = useState(null);
    const [verifyOpen, setVerifyOpen] = useState(false);
    const [verifyForm, setVerifyForm] = useState({ verifiedStatus: 'verified', verificationSource: 'Nursys QuickConfirm', licenseExpiration: '', disciplinaryFlag: false, notes: '' });
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });
    const [processing, setProcessing] = useState(false);
    const [diditStatus, setDiditStatus] = useState(null);
    const { admin } = useContext(AdminPageContext);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const [actionAnchorEl, setActionAnchorEl] = useState(null);
    const [actionNurse, setActionNurse] = useState(null);

    const handleOpenActions = (event, nurse) => {
        setActionAnchorEl(event.currentTarget);
        setActionNurse(nurse);
    };
    const handleCloseActions = () => {
        setActionAnchorEl(null);
        setActionNurse(null);
    };

    // Load nurses for admin review - extract to callable function so it can be reused
    const loadNurses = async () => {
        if (!functionsInstance) {
            setError('Cloud Functions not initialized');
            return;
        }
        try {
            const adminListNurses = httpsCallable(functionsInstance, 'adminListNurses');
            const result = await adminListNurses();
            const list = Array.isArray(result.data && result.data.nurses) ? result.data.nurses : [];
            setNurses(list);
        } catch (err) {
            console.error('Error loading nurses via callable', err);
            setError('Error loading nurses');
        }
    };

    // Safely format various possible licenseExpiration values to YYYY-MM-DD or empty string
    const formatLicenseExpiration = (val) => {
        if (!val) return '';
        try {
            let date = null;
            if (typeof val.toDate === 'function') {
                date = val.toDate();
            } else if (val.seconds) {
                date = new Date(val.seconds * 1000);
            } else if (val._seconds) {
                date = new Date(val._seconds * 1000);
            } else {
                date = new Date(val);
            }
            if (!date || isNaN(date.getTime())) return '';
            return date.toISOString().slice(0,10);
        } catch (e) {
            return '';
        }
    };
    //console.log('authUser ', authUser);
    console.log('admin', admin);

    useEffect(() => {
        if (admin === 'super') {
                setLoaded(true);
        } else {
            setLoaded(true);
        }
    }, [auth.currentUser.uid, admin, functionsInstance, firestoreInstance]);

    // Load nurses for admin review
    useEffect(() => {
        // loadNurses is defined as a callable function in component scope
        loadNurses();
    }, [firestoreInstance]);

    const handleVerifyClose = () => {
        setVerifyOpen(false);
        setSelectedNurse(null);
        setDiditStatus(null);
    };

    const handleVerifyChange = (field, value) => {
        setVerifyForm((prev) => ({ ...prev, [field]: value }));
    };

    const handleOpenNursys = (nurse) => {
        // Basic QuickConfirm link with license number — admin will complete lookup manually
        const url = nurse.licenseNumber ? `https://www.nursys.com/QuickConfirm/LicenseLookup?LicenseNumber=${encodeURIComponent(nurse.licenseNumber)}` : 'https://www.nursys.com/QuickConfirm/';
        window.open(url, '_blank');
    };

    const handleOpenStateLookup = (nurse) => {
        // Open a Google search for the state's board lookup as a helpful shortcut
        const state = nurse.stateOfLicensure || (nurse.states ? nurse.states[0] : '');
        const q = `${state} board of nursing license lookup ${nurse.licenseNumber || ''}`;
        const url = `https://www.google.com/search?q=${encodeURIComponent(q)}`;
        window.open(url, '_blank');
    };

    const handleSubmitVerification = async () => {
        if (!selectedNurse || !firestoreInstance) return;
        setProcessing(true);
        try {
                // Use HTTPS callable to perform the update in a trusted environment
                if (!functionsInstance) throw new Error('Functions not initialized');
                const adminUpdateNurseVerification = httpsCallable(functionsInstance, 'adminUpdateNurseVerification');
                await adminUpdateNurseVerification({
                    nurseId: selectedNurse.id,
                    verifiedStatus: verifyForm.verifiedStatus,
                    verificationSource: verifyForm.verificationSource,
                    licenseExpiration: verifyForm.licenseExpiration || null,
                    disciplinaryFlag: !!verifyForm.disciplinaryFlag,
                    notes: verifyForm.notes || ''
                });

            setSnackbar({ open: true, message: 'Verification saved', severity: 'success' });
            handleVerifyClose();
            // reload nurses
            await loadNurses();
        } catch (err) {
            console.error('Error saving verification', err);
            setSnackbar({ open: true, message: 'Error saving verification', severity: 'error' });
        }
        setProcessing(false);
    };

    // Load Didit verification status when a nurse is selected
    useEffect(() => {
        const loadDiditStatus = async () => {
            if (!selectedNurse || !firestoreInstance) return;
            try {
                const dRef = doc(firestoreInstance, 'didit_verifications', selectedNurse.id);
                const snap = await getDoc(dRef);
                if (snap.exists()) {
                    const data = snap.data();
                    setDiditStatus(data.status || null);
                } else {
                    setDiditStatus(null);
                }
            } catch (e) {
                console.warn('Failed to load Didit verification status', e);
                setDiditStatus(null);
            }
        };
        loadDiditStatus();
    }, [selectedNurse, firestoreInstance]);

    const renderStatusChip = (status) => {
        if (!status) return <Chip label="unverified" size="small" variant="outlined" />;
        const s = status.toLowerCase();
        if (s === 'verified') return <Chip icon={<CheckIcon fontSize="small" sx={{ color: 'inherit' }} />} label="Verified" size="small" sx={{ bgcolor: 'success.main', color: 'common.white' }} />;
        if (s === 'pending') return <Chip icon={<HourglassEmptyIcon fontSize="small" sx={{ color: 'inherit' }} />} label="Pending" size="small" sx={{ bgcolor: 'warning.main', color: 'text.primary' }} />;
        if (s === 'rejected') return <Chip icon={<CancelIcon fontSize="small" sx={{ color: 'inherit' }} />} label="Rejected" size="small" sx={{ bgcolor: 'error.main', color: 'common.white' }} />;
        return <Chip label={status} size="small" variant="outlined" />;
    };

    const renderDiditStatusChip = (status) => {
        if (!status) return <Chip icon={<HelpOutlineIcon fontSize="small" />} label="Didit: none" size="small" variant="outlined" />;
        const s = (status || '').toLowerCase().trim();
        // treat common variants like "approved" as verified
        const verifiedValues = ['verified', 'approved', 'approve', 'clear', 'cleared', 'ok', 'success'];
        if (verifiedValues.includes(s)) return <Chip icon={<CheckIcon fontSize="small" sx={{ color: 'inherit' }} />} label="Didit: Verified" size="small" sx={{ bgcolor: 'success.main', color: 'common.white' }} />;
        if (s === 'pending' || s === 'processing' || s === 'in_progress') return <Chip icon={<HourglassEmptyIcon fontSize="small" sx={{ color: 'inherit' }} />} label="Didit: Pending" size="small" sx={{ bgcolor: 'warning.main', color: 'text.primary' }} />;
        if (s === 'failed' || s === 'rejected' || s === 'denied' || s === 'error') return <Chip icon={<CancelIcon fontSize="small" sx={{ color: 'inherit' }} />} label={`Didit: ${status}`} size="small" sx={{ bgcolor: 'error.main', color: 'common.white' }} />;
        return <Chip icon={<HelpOutlineIcon fontSize="small" sx={{ color: 'inherit' }} />} label={`Didit: ${status}`} size="small" variant="outlined" />;
    };

    return (
        <>
        {loaded ? (
            <Container maxWidth={false} disableGutters>
                <SetPageTitle title="Admin Panel" />

                <Box sx={{ p: 2, width: '100%'}}>
                    <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 1 }}>Admin Console</Typography>
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>Manage site users and nurse license verifications.</Typography>
                </Box>

                <PageSection>
                    {error !== null && <Alert severity="error">{error}</Alert>}

                    <Grid container spacing={3}>
                        <Grid xs={12} md={8}>
                            <Paper sx={{ p: 2 }}>
                                {admin === 'super' ? (
                                    <>
                                    <Typography variant="h6" sx={{ mb: 2 }}>Website Admin Console</Typography>
                                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>Full administrative controls.</Typography>
                                    </>
                                ) : admin === 'manager' ? (
                                    <Box sx={{height: '70vh', p: 10, alignContent: 'center', textAlign: 'center'}}>Business Co-operator Access Permission</Box>
                                ) : (
                                    <Typography>Access Permission is required.</Typography>
                                )}
                            </Paper>
                        </Grid>

                        <Grid xs={12} md={4}>
                            <Paper sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
                                <PeopleIcon color="primary" sx={{ fontSize: 36 }} />
                                <Box>
                                    <Typography variant="subtitle1">Access Level</Typography>
                                    <Typography variant="body2">{admin}</Typography>
                                    <Typography variant="caption" sx={{ display: 'block', mt: 1 }}>{nurses.length || 0} nurses</Typography>
                                </Box>
                            </Paper>
                        </Grid>
                    </Grid>
                </PageSection>

                {admin === 'super' && (
                    <PageSection>
                        <Typography variant="h6" sx={{ mb: 1 }}>Nurse License Verifications</Typography>
                        <Paper sx={{ p: 2 }}>
                            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
                                <TextField size="small" placeholder="Search nurses..." InputProps={{ startAdornment: <SearchIcon sx={{ mr: 1 }} /> }} sx={{ flex: 1 }} onChange={(e) => {
                                    const q = e.target.value.toLowerCase();
                                    if (!q) return setNurses((prev) => prev);
                                    setNurses((prev) => prev.filter(n => (`${n.fullName||''} ${n.email||''} ${n.licenseNumber||''}`).toLowerCase().includes(q)));
                                }} />
                                <Tooltip title="Refresh list">
                                    <IconButton onClick={async () => { await loadNurses(); }}>
                                        <RefreshIcon />
                                    </IconButton>
                                </Tooltip>
                                <Button startIcon={<FactCheckIcon />} variant="contained" onClick={async () => {
                                    // Quick action: open first nurse verify dialog
                                    if (!nurses || nurses.length === 0) return;
                                    const nurse = nurses[0];
                                    setSelectedNurse(nurse);
                                    setVerifyForm({
                                        verifiedStatus: nurse.verifiedStatus || 'pending',
                                        verificationSource: nurse.verificationSource || 'Nursys QuickConfirm',
                                        licenseExpiration: formatLicenseExpiration(nurse.licenseExpiration),
                                        disciplinaryFlag: !!nurse.disciplinaryFlag,
                                        notes: nurse.notes || ''
                                    });
                                    setVerifyOpen(true);
                                }}>Verify First</Button>
                            </Stack>

                            {nurses.length === 0 ? (
                                <Typography variant="body2">No nurse records found.</Typography>
                            ) : (
                                <List>
                                    {nurses.map((nurse) => (
                                        <React.Fragment key={nurse.id}>
                                            <ListItem secondaryAction={(!isMobile) ? (
                                                <Stack direction="row" spacing={1}>
                                                    <Tooltip title="Open Nursys QuickConfirm">
                                                        <IconButton size="small" aria-label="Open Nursys QuickConfirm" color="info" onClick={() => handleOpenNursys(nurse)}>
                                                            <OpenInNewIcon />
                                                        </IconButton>
                                                    </Tooltip>
                                                    <Tooltip title="Open State Board Lookup">
                                                        <IconButton size="small" aria-label="Open State Board Lookup" color="info" onClick={() => handleOpenStateLookup(nurse)}>
                                                            <PublicIcon />
                                                        </IconButton>
                                                    </Tooltip>
                                                    <Button startIcon={<FactCheckIcon />} variant="outlined" size="small" onClick={() => {
                                                        setSelectedNurse(nurse);
                                                        setVerifyForm({
                                                            verifiedStatus: nurse.verifiedStatus || 'pending',
                                                            verificationSource: nurse.verificationSource || 'Nursys QuickConfirm',
                                                            licenseExpiration: formatLicenseExpiration(nurse.licenseExpiration),
                                                            disciplinaryFlag: !!nurse.disciplinaryFlag,
                                                            notes: nurse.notes || ''
                                                        });
                                                        setVerifyOpen(true);
                                                    }}>Verify</Button>
                                                </Stack>
                                            ) : null}>
                                                <ListItemAvatar>
                                                    <Avatar>{(nurse.fullName || '??').split(' ').map(s=>s[0]).slice(0,2).join('')}</Avatar>
                                                </ListItemAvatar>
                                                <ListItemText primary={<Stack direction="row" spacing={1} alignItems="center"><Typography>{nurse.fullName || '(no name)'}</Typography>{renderStatusChip(nurse.verifiedStatus)}</Stack>} secondary={`${nurse.licenseType || ''} ${nurse.licenseNumber || ''} • ${nurse.email || ''} • ${nurse.stateOfLicensure || (nurse.states ? nurse.states.join(', ') : '')}`} />
                                            </ListItem>
                                                {isMobile && (
                                                    <Box sx={{ px: 2, pb: 1 }}>
                                                        <Stack direction="row" spacing={1}>
                                                            <Tooltip title="Open Nursys QuickConfirm">
                                                                <IconButton size="small" aria-label="Open Nursys QuickConfirm" color="info" onClick={() => handleOpenNursys(nurse)}>
                                                                    <OpenInNewIcon />
                                                                </IconButton>
                                                            </Tooltip>
                                                            <Tooltip title="Open State Board Lookup">
                                                                <IconButton size="small" aria-label="Open State Board Lookup" color="info" onClick={() => handleOpenStateLookup(nurse)}>
                                                                    <PublicIcon />
                                                                </IconButton>
                                                            </Tooltip>
                                                            <Button startIcon={<FactCheckIcon />} variant="outlined" size="small" onClick={() => {
                                                                setSelectedNurse(nurse);
                                                                setVerifyForm({
                                                                    verifiedStatus: nurse.verifiedStatus || 'pending',
                                                                    verificationSource: nurse.verificationSource || 'Nursys QuickConfirm',
                                                                    licenseExpiration: formatLicenseExpiration(nurse.licenseExpiration),
                                                                    disciplinaryFlag: !!nurse.disciplinaryFlag,
                                                                    notes: nurse.notes || ''
                                                                });
                                                                setVerifyOpen(true);
                                                            }}>Verify</Button>
                                                        </Stack>
                                                    </Box>
                                                )}
                                            <Divider />
                                        </React.Fragment>
                                    ))}
                                </List>
                            )}
                        </Paper>
                    </PageSection>
                )}

                {/* Mobile actions menu */}
                <Menu anchorEl={actionAnchorEl} open={Boolean(actionAnchorEl)} onClose={handleCloseActions} keepMounted>
                    <MenuItem onClick={() => { if (actionNurse) handleOpenNursys(actionNurse); handleCloseActions(); }}>Open Nursys QuickConfirm</MenuItem>
                    <MenuItem onClick={() => { if (actionNurse) handleOpenStateLookup(actionNurse); handleCloseActions(); }}>Open State Board Lookup</MenuItem>
                    <MenuItem onClick={() => {
                        if (actionNurse) {
                            setSelectedNurse(actionNurse);
                            setVerifyForm({
                                verifiedStatus: actionNurse.verifiedStatus || 'pending',
                                verificationSource: actionNurse.verificationSource || 'Nursys QuickConfirm',
                                licenseExpiration: formatLicenseExpiration(actionNurse.licenseExpiration),
                                disciplinaryFlag: !!actionNurse.disciplinaryFlag,
                                notes: actionNurse.notes || ''
                            });
                            setVerifyOpen(true);
                        }
                        handleCloseActions();
                    }}>Verify</MenuItem>
                </Menu>

                {/* Verification Dialog */}
                <Dialog open={verifyOpen} onClose={handleVerifyClose} fullWidth maxWidth="sm">
                    <DialogTitle>Verify Nurse — {selectedNurse?.fullName}</DialogTitle>
                    <DialogContent>
                        {selectedNurse && (
                            <Box sx={{ mt: 1 }}>
                                <Typography variant="subtitle1">Email: {selectedNurse.email}</Typography>
                                <Box sx={{ mt: 0.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                                    {renderDiditStatusChip(diditStatus)}
                                    <Tooltip title="Open Didit">
                                        <IconButton size="small" aria-label="Open Didit" onClick={() => window.open('https://business.didit.me/', '_blank')}>
                                            <OpenInNewIcon fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                </Box>
                                <Typography variant="subtitle2">License: {selectedNurse.licenseNumber} ({selectedNurse.licenseType})</Typography>
                                <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
                                    <Button variant="outlined" onClick={() => handleOpenNursys(selectedNurse)}>Open Nursys QuickConfirm</Button>
                                    <Button variant="outlined" onClick={() => handleOpenStateLookup(selectedNurse)}>Open State Board Lookup</Button>
                                </Stack>

                                <Box component="form" sx={{ mt: 2 }}>
                                    <FormControl fullWidth sx={{ mt: 1 }}>
                                        <InputLabel>Result</InputLabel>
                                        <Select value={verifyForm.verifiedStatus} label="Result" onChange={(e) => handleVerifyChange('verifiedStatus', e.target.value)}>
                                            <MenuItem value="verified">Verified</MenuItem>
                                            <MenuItem value="rejected">Rejected</MenuItem>
                                            <MenuItem value="pending">Pending</MenuItem>
                                        </Select>
                                    </FormControl>

                                    <FormControl fullWidth sx={{ mt: 1 }}>
                                        <InputLabel>Verification Source</InputLabel>
                                        <Select value={verifyForm.verificationSource} label="Verification Source" onChange={(e) => handleVerifyChange('verificationSource', e.target.value)}>
                                            <MenuItem value="Nursys QuickConfirm">Nursys QuickConfirm</MenuItem>
                                            <MenuItem value="State BON">State BON</MenuItem>
                                            <MenuItem value="Nursys e-Notify">Nursys e-Notify</MenuItem>
                                        </Select>
                                    </FormControl>

                                    <TextField fullWidth label="License Expiration" type="date" sx={{ mt: 1 }} InputLabelProps={{ shrink: true }} value={verifyForm.licenseExpiration} onChange={(e) => handleVerifyChange('licenseExpiration', e.target.value)} />

                                    <FormControlLabel control={<Checkbox checked={verifyForm.disciplinaryFlag} onChange={(e) => handleVerifyChange('disciplinaryFlag', e.target.checked)} />} label="Disciplinary Flag" sx={{ mt: 1 }} />

                                    <TextField fullWidth label="Notes" multiline rows={3} sx={{ mt: 1 }} value={verifyForm.notes} onChange={(e) => handleVerifyChange('notes', e.target.value)} />
                                </Box>
                            </Box>
                        )}
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={handleVerifyClose}>Cancel</Button>
                        <LoadingButton loading={processing} variant="contained" onClick={handleSubmitVerification}>Save Verification</LoadingButton>
                    </DialogActions>
                </Dialog>

                <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar((s) => ({ ...s, open: false }))} message={snackbar.message} />
            </Container>
        ) : (
            <Box sx={{width: '100%', p: 5, textAlign:'center'}}><CircularProgress /></Box>
        )}
        </>
    )
}
